import { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Decor } from "@/components/Decor";
import { Disclaimer } from "@/components/Disclaimer";
import { QuestionCard } from "@/components/QuestionCard";
import { ThinkingLoader } from "@/components/ThinkingLoader";
import { VideoAnswer } from "@/components/VideoAnswer";
import { randomQuestionPageUrl } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { loadYouTubeApi } from "@/lib/youtube";

export default function Home({ initialEntry = null }) {
  const entry = initialEntry;
  const [phase, setPhase] = useState(entry ? "question" : "idle");

  useEffect(() => {
    if (!entry) return;
    loadYouTubeApi();
    let thinkingTimer;
    let videoTimer;
    const schedule = () => {
      thinkingTimer = setTimeout(() => {
        setPhase("thinking");
        videoTimer = setTimeout(() => {
          setPhase("video");
          trackEvent("video_shown", entry);
        }, 5000);
      }, 2000);
    };
    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });
    return () => {
      window.removeEventListener("load", schedule);
      clearTimeout(thinkingTimer);
      clearTimeout(videoTimer);
    };
  }, [entry]);

  const nextUrl = randomQuestionPageUrl(entry?.slug);

  return (
    <main className="stage-bg grain relative min-h-screen overflow-hidden px-4 py-10 sm:px-8 sm:py-14">
      <Decor />
      <div className="relative mx-auto max-w-3xl">
        <section className="marquee-frame px-6 py-10 sm:px-12 sm:py-14" data-testid="wisdom-box">
          <div className="bulbs top" />
          <div className="bulbs bottom" />
          <h1 data-testid="home-title" className="font-marquee title-glow leading-tight text-white">
            <span className="block text-5xl text-amber-400 sm:text-6xl lg:text-7xl">BollyGPT</span>
            <span className="mt-3 block text-2xl sm:text-3xl" data-testid="home-tagline">wisdom for life’s important questions and situations</span>
          </h1>

          <div className="mt-10 space-y-8" data-testid="answer-section">
            {!entry ? (
              <a
                data-testid="show-me-button"
                href={nextUrl}
                onClick={() => trackEvent("show_me_click", { english_question: "" })}
                className="show-btn font-marquee inline-flex items-center rounded-full px-10 py-4 text-2xl whitespace-nowrap text-white"
              >
                Show me
              </a>
            ) : (
              <QuestionCard key={entry.id} entry={entry} />
            )}
            <AnimatePresence mode="wait">
              {phase === "thinking" && <ThinkingLoader key="t" />}
              {phase === "video" && <VideoAnswer key={entry.id} entry={entry} />}
            </AnimatePresence>
          </div>
        </section>

        {entry && <div className="mt-14 text-center">
          <a
            data-testid="more-gyan-button"
            href={nextUrl}
            onClick={() => trackEvent("more_gyan_click", entry)}
            className="show-btn font-marquee inline-flex items-center rounded-full px-8 py-4 text-xl whitespace-nowrap text-white sm:px-10 sm:text-2xl"
          >
            Give me more gyan
          </a>
        </div>}
        <Disclaimer />
      </div>
    </main>
  );
}
